import React, { useCallback } from 'react';
import { Platform, View, StyleSheet } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated';
import { Image, ImageLoadEventData } from 'expo-image';
import { setCachedDimensions } from './imageDimensionsCache';

const AnimatedImage = Animated.createAnimatedComponent(Image);

interface IImageViewerContentProps {
  uri: string;
  // Morph rect SVs (drive the position/size of the morphing image).
  animLeft: SharedValue<number>;
  animTop: SharedValue<number>;
  animWidth: SharedValue<number>;
  animHeight: SharedValue<number>;
  animOpacity: SharedValue<number>;
}

// Morph overlay: a single image absolutely positioned by the morph rect SVs,
// used only during the open and close transitions. The interactive carousel
// renders behind/in-front of this overlay; once the open animation completes
// the overlay is unmounted and the carousel takes over.
export const ImageViewerContent: React.FC<IImageViewerContentProps> = ({
  uri,
  animLeft,
  animTop,
  animWidth,
  animHeight,
  animOpacity,
}) => {
  const handleImageLoad = useCallback((event: ImageLoadEventData) => {
    const source = event?.source;
    if (source?.width && source?.height) {
      setCachedDimensions(uri, source.width, source.height);
    }
  }, [uri]);

  const imageAnimatedStyle = useAnimatedStyle(() => {
    const style: any = {
      position: 'absolute',
      left: animLeft.value,
      top: animTop.value,
      width: animWidth.value,
      height: animHeight.value,
      opacity: animOpacity.value,
    };
    if (Platform.OS === 'web') {
      style.willChange = 'transform, opacity';
      style.backfaceVisibility = 'hidden';
    }
    return style;
  });

  return (
    <View
      style={StyleSheet.absoluteFill}
      pointerEvents="none"
      className="web:[&_img]:pointer-events-none"
    >
      <AnimatedImage
        source={{ uri }}
        contentFit="contain"
        style={imageAnimatedStyle}
        onLoad={handleImageLoad}
      />
    </View>
  );
};
